import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Typography,
  IconButton,
  CircularProgress,
} from "@mui/material";
import axios from "../../utils/axiosInstance";
import { toast } from "react-toastify";
import { sortAlphabetically } from "../../utils/sortAlphabetically";
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import { getUIDescription } from "./customerGroupDetails/AssigningCustomerGroupDevice";
export default function AddAppForm({ open, onClose, onSubmit, onAddApp }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [logo, setLogo] = useState("");
  const [logoStatus, setLogStatus] = useState("#000000");
  const [url, setUrl] = useState("");
  const [groupId, setGroupId] = useState("");
  const [appType, setAppType] = useState("admin"); // "admin" | "user" | "viewControl" | "viewOnly"

  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    name: false,
    description: false,
    logo: false,
    logoStatus: false,
    url: false,
    groupId: false,
  });

  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/svg+xml",
    ];
    if (!validTypes.includes(file.type)) {
      toast.error("Please upload a valid image file (JPEG, PNG, GIF, or SVG)");
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }

    setSelectedFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Upload logo to server
  const handleUploadLogo = async () => {
    if (!selectedFile) {
      toast.warning("Please select a file first");
      return;
    }

    setUploadingLogo(true);

    let FullDescription = `${getUIDescription()}\n Upload Logo Application`;
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("folder", "logos");
      formData.append("UI_Description", FullDescription);
      const response = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data.success && response.data.url) {
        setLogo(response.data.url);
        setPreviewUrl(response.data.url);
        toast.success("✅ Logo uploaded successfully!");
        setErrors((prev) => ({ ...prev, logo: false }));
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      console.error("❌ Error uploading logo:", error);
      toast.error("Failed to upload logo. Please try again.");
    } finally {
      setUploadingLogo(false);
      setSelectedFile(null);
    }
  };

  // Remove logo
  const handleRemoveLogo = () => {
    setLogo("");
    setPreviewUrl("");
    setSelectedFile(null);
    setErrors((prev) => ({ ...prev, logo: false }));
  };

  useEffect(() => {
    const urlMap = {
    admin:        `${process.env.REACT_APP_API_URL}/ZWAD`,
    user:         `${process.env.REACT_APP_API_URL}/ZXUR`,
    viewOnly:     `${process.env.REACT_APP_API_URL}/ZXVO`,
    };
    setUrl(urlMap[appType] ?? "");
  }, [appType]);

  const groupRequired = appType !== "admin";

  // Fetch groups
  useEffect(() => {
    if (!open) return;

    const fetchGroups = async () => {
      try {
        setLoadingGroups(true);
        const token = localStorage.getItem("token");

        // Fetch both customer and geographical groups in parallel
        const [customerRes, geoRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_GATEAWAY}/groups`, {
            params: {
              companyId: process.env.REACT_APP_COMPANY_ID,
              type: "customer",
            },
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${process.env.REACT_APP_API_GATEAWAY}/groups`, {
            params: {
              companyId: process.env.REACT_APP_COMPANY_ID,
              type: "geographical",
            },
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        const customerGroups = Array.isArray(customerRes.data)
          ? customerRes.data
          : [];
        const geoGroups = Array.isArray(geoRes.data) ? geoRes.data : [];

        // Merge both lists
        const mergedGroups = sortAlphabetically(
          [...customerGroups, ...geoGroups],
          "name",
        );
        setGroups(mergedGroups);
      } catch (err) {
        console.error("❌ Error fetching groups:", err);
        setGroups([]);
      } finally {
        setLoadingGroups(false);
      }
    };

    fetchGroups();
  }, [open]);

  // Validators
  const isValidUrl = (str) => {
    try {
      new URL(str);
      return true;
    } catch {
      return false;
    }
  };

  const isValidHexColor = (color) => /^#([0-9A-F]{3}){1,2}$/i.test(color);

  const validateForm = () => {
    console.log(name, description, logo, logoStatus, url, groupId);
    const newErrors = {
      name: name.trim() === "",
      description: false,
      logo: logo.trim() !== "" && !isValidUrl(logo),
      logoStatus: logoStatus.trim() === "" || !isValidHexColor(logoStatus),
      url: url.trim() === "" || !isValidUrl(url),
      groupId: groupRequired && !groupId,
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  const handleClose = async () => {
    setName("");
    setDescription("");
    setLogo("");
    setLogStatus("#000");
    setPreviewUrl("");
    setUrl("");
    setAppType("admin");
    setGroupId("");
    setErrors({});
    onClose();
  };
  // Submit handler
  const handleAddApp = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      const payload = {
        companyId: process.env.REACT_APP_COMPANY_ID,
        name,
        description,
        logo,
        logoStatus,
        url,
        groupId: groupRequired ? groupId || null : null,
        UI_Description: `${getUIDescription()}\n Add Application`,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/applications/`,
        payload,
      );

      toast.success("✅ Application created successfully!");
      onSubmit?.(response.data);
      onAddApp?.();

      // Reset form
      setName("");
      setDescription("");
      setLogo("");
      setLogStatus("#000000");
      setUrl("");
      setGroupId("");
      setErrors({});
      onClose();
    } catch (error) {
      const rawMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.response?.data ||
        error.message;

      let errorMessage = "Failed to create application";

      if (typeof rawMessage === "string") {
        const lowerMsg = rawMessage.toLowerCase();
        if (lowerMsg.includes("url")) {
          errorMessage = "Please enter a valid URL";
        } else if (lowerMsg.includes("group")) {
          errorMessage = "Please select a valid group";
        } else {
          errorMessage = rawMessage;
        }
      }

      toast.error(errorMessage);
      console.error("❌ Error creating app:", rawMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add New Application</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (errors.name && e.target.value.trim()) {
                setErrors((prev) => ({ ...prev, name: false }));
              }
            }}
            fullWidth
            required
            error={errors.name}
            helperText={errors.name ? "Name is required" : ""}
          />

          <TextField
            label="Description"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              if (errors.description && e.target.value.trim()) {
                setErrors((prev) => ({ ...prev, description: false }));
              }
            }}
            fullWidth
            // required
            multiline
            minRows={2}
            error={errors.description}
            helperText={errors.description ? "Description is required" : ""}
          />

          {/* Logo Upload Section */}
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ mb: 1, fontWeight: 500, color: "text.secondary" }}
            >
              Upload Logo (Recommended Aspect Ratio: 4.5:1)
            </Typography>

            {/* Preview */}
            {previewUrl && (
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  maxWidth: 200,
                  height: 120,
                  mb: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#f5f5f5",
                }}
              >
                <img
                  src={previewUrl}
                  alt="Logo preview"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
                <IconButton
                  size="small"
                  onClick={handleRemoveLogo}
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    backgroundColor: "rgba(0,0,0,0.6)",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "rgba(0,0,0,0.8)",
                    },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}

            {/* File Input */}
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUploadIcon />}
                disabled={uploadingLogo}
              >
                Choose File
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleFileSelect}
                />
              </Button>

              {selectedFile && (
                <Button
                  variant="contained"
                  onClick={handleUploadLogo}
                  disabled={uploadingLogo}
                  startIcon={
                    uploadingLogo ? <CircularProgress size={16} /> : null
                  }
                >
                  {uploadingLogo ? "Uploading..." : "Upload"}
                </Button>
              )}
            </Box>

            {selectedFile && (
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ mt: 0.5 }}
              >
                Selected: {selectedFile.name}
              </Typography>
            )}

            {errors.logo && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 0.5, display: "block" }}
              >
                Please upload a valid logo
              </Typography>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <TextField
              label="Banner Color"
              type="color"
              value={logoStatus}
              onChange={(e) => {
                setLogStatus(e.target.value);
                if (errors.logoStatus && isValidHexColor(e.target.value)) {
                  setErrors((prev) => ({ ...prev, logoStatus: false }));
                }
              }}
              fullWidth
              required
              error={errors.logoStatus}
              helperText={
                errors.logoStatus
                  ? "Enter a valid color hex (e.g. #050505)"
                  : "Select a color for the banner"
              }
              InputLabelProps={{ shrink: true }}
            />

            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "8px",
                border: "1px solid #ccc",
                backgroundColor: logoStatus,
              }}
            />
          </Box>

          <TextField
            select
            label="App Type"
            value={appType}
            onChange={(e) => {
              setAppType(e.target.value);
              if (e.target.value === "admin") setGroupId(""); // ← clear group on admin select
            }}
            fullWidth
            required
          >
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="user">View & Control</MenuItem>
            <MenuItem value="viewOnly">View Only</MenuItem>
          </TextField>

          <TextField
            select
            // label="Group"
            value={appType === "admin" ? "" : groupId}
            onChange={(e) => setGroupId(e.target.value)}
            fullWidth
            required={groupRequired}
            disabled={loadingGroups || appType === "admin"}
            error={errors.groupId}
            helperText={errors.groupId ? "Group is required for this app type" : ""}
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="">No group selected</MenuItem>
            {Array.isArray(groups) && groups.length > 0 ? (
              groups.map((group) => (
                <MenuItem key={group.id} value={group.id}>
                  {group.name}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>
                {loadingGroups ? "Loading groups..." : "No groups available"}
              </MenuItem>
            )}
          </TextField>

          <TextField
            label="App URL"
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              if (errors.url && isValidUrl(e.target.value)) {
                setErrors((prev) => ({ ...prev, url: false }));
              }
            }}
            fullWidth
            error={errors.url}
            helperText={
              errors.url
                ? url.trim() === ""
                  ? "App URL is required"
                  : "Enter a valid URL"
                : ""
            }
            disabled
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          sx={{
            borderRadius: 0, // ✅ remove rounding
          }}
          onClick={handleClose}
          color="error"
        >
          Cancel
        </Button>
        <Button
          onClick={handleAddApp}
          variant="contained"
          color="primary"
          loading={loading}
          disabled={
            loadingGroups ||
            !name.trim() ||
            !logoStatus.trim() ||
            !url.trim() ||
            (groupRequired && !groupId)  // group only required for user/viewOnly
          }
        >
          Add App
        </Button>
      </DialogActions>
    </Dialog>
  );
}
